import React, { useState, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import { paymentMethodsSchema, PaymentMethodsValues } from "@/schemas/sellerProfileSchema";
import { PaymentMethodDisplay } from "../paymentMethodDisplay/PaymentMethodDisplay";
import { PaymentMethodForm } from "../paymentMethodForm/PaymentMethodForm";
import { PaymentMethodDialog } from "../paymentMethodDialog/PaymentMethodDialog";
import { SellerPaymentInfo } from "@/types/AppTypes";
import { executeGraphQLMutation } from "@/utils/helper/graphql-helper";
import { useQueryClient } from "@tanstack/react-query";
import { createSellerPaymentInfo, updateSellerPaymentInfo } from "@/graphql/mutations";
import { v4 as uuidv4 } from "uuid";
import { LoaderCircle } from "lucide-react";
import { toast } from "sonner";

interface SellerPaymentMethodProps {
  paymentMethods?: SellerPaymentInfo;
  sellerId: string;
  loading: boolean;
}

export const SellerPaymentMethod = ({
  paymentMethods,
  sellerId,
  loading,
}: SellerPaymentMethodProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const [isUpdating, setIsUpdating] = useState(false);

  const queryClient = useQueryClient();
  const form = useForm<PaymentMethodsValues>({
    resolver: zodResolver(paymentMethodsSchema),
    mode: "onSubmit",
    defaultValues: {
      accountHolderName: paymentMethods?.account_holder_name || "",
      bankName: paymentMethods?.bank_name || "",
      routingNumber: paymentMethods?.routing_number || "",
      accountNumber: paymentMethods?.account_number || "",
      paypalConnected: false,
      venmoConnected: false,
      cashAppConnected: false,
    },
  });

  // Update form default values when paymentMethods prop changes
  useEffect(() => {
    if (paymentMethods) {
      form.reset({
        accountHolderName: paymentMethods.account_holder_name || "",
        bankName: paymentMethods.bank_name || "",
        routingNumber: paymentMethods.routing_number || "",
        accountNumber: paymentMethods.account_number || "",
        paypalConnected: false,
        venmoConnected: false,
        cashAppConnected: false,
      });
    }
  }, [paymentMethods, form]);

  const onSubmit = async (data: PaymentMethodsValues) => {
    setIsUpdating(true);
    const { data: updatedData } = await executeGraphQLMutation({
      query: paymentMethods?.id ? updateSellerPaymentInfo : createSellerPaymentInfo,
      variables: {  
        input: {
          id: paymentMethods?.id || uuidv4(),
          account_holder_name: data.accountHolderName,
          bank_name: data.bankName,
          account_number: data.accountNumber,
          routing_number: data.routingNumber,
          seller_id: sellerId,
          created_at: paymentMethods?.created_at || new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
      },
    });

    // Invalidate the query to force a refetch and update the UI
    await queryClient.invalidateQueries({
      queryKey: ["seller_payment_methods", { seller_id: { eq: sellerId } }]
    });
    
    // Close dialog if it's open
    setIsDialogOpen(false);
    
    // Show success message
    toast.success(paymentMethods?.id ? "Payment method updated successfully!" : "Payment method added successfully!");
    
    form.reset();

    setIsUpdating(false);
  };

  const handleUpdateClick = () => {
    if (paymentMethods) {
      form.reset({
        accountHolderName: paymentMethods?.account_holder_name || "",
        bankName: paymentMethods?.bank_name || "",
        routingNumber: "",
        accountNumber: paymentMethods?.account_number || "",
        paypalConnected: false,
        venmoConnected: false,
        cashAppConnected: false,
      });
    }
    setIsDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center w-full h-[40vh]">
        <LoaderCircle className="w-6 h-6 animate-spin mr-4 "></LoaderCircle>
        Fetching Payment Method. This won’t take long!
      </div>
    );
  }

  if (paymentMethods) {
    return (
      <>
        <PaymentMethodDisplay paymentInfo={paymentMethods} onUpdateClick={handleUpdateClick} />

        <PaymentMethodDialog
          isUpdating={isUpdating}
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          form={form}
          onSubmit={onSubmit}
        />
      </>
    );
  }

  return (
    <PaymentMethodForm
      isUpdating={isUpdating}
      form={form}
      onSubmit={onSubmit}
      submitButtonText="Save Payment Information"
      showOtherPaymentMethods={true}
    />
  );
};
